// ForecastLayout – textové předpovědi CHMI. Výběr kategorie (ČR/Kraje/Hory), u Kraje/Hory
// navíc výběr regionu z listboxu, pod tím všechny textové předpovědi pro danou volbu
// (dnes/zítra/noc/další dny…) poskládané pod sebe. Data z GET /api/forecast (cw-server,
// viz doc/plans/forecast-text-page.md a cw-server/doc/plans/forecast-download.md) — natáhnou
// se všechny kategorie najednou, filtrování na klientovi, žádný re-fetch při přepínání.
//
// Karta NENÍ obecné zobrazení properties.data[] — věrně reprodukuje textový formát ČHMÚ
// (nadpis karty = headline-main.headline, obsah = data[] jako "headline: text" na řádek),
// s jednou výjimkou u Hor, kde se dvě API položky ručně slučují do jedné sekce
// (Srážky = pravděpodobnost+úhrn, Teploty = min+max), protože tak to ČHMÚ ve svém
// textovém formátu prezentuje.
import { useEffect, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Spinner } from '@heroui/react'
import { useForecastCategories } from '../hooks/useForecast'
import { getLastForecastSelection, setLastForecastSelection } from '../hooks/useLastForecastSelection'
import { UserIcon } from '../components/auth/UserIcon'
import { useTrackView } from '../hooks/useTrackProduct'

const CATEGORIES = [
  { value: 'CR', label: 'ČR' },
  { value: 'Kraje', label: 'Kraje' },
  { value: 'Hory', label: 'Hory' },
]

const CR_RPP_OFFICE = 'ČHMÚ, KOMO-PRAHA'

// Hor-specifické sloučení dvou API položek do jedné zobrazené sekce (viz hlavičkový komentář).
const HORY_MERGED_GROUPS = [
  { names: ['textPrecProbability', 'textPrecipitation'], heading: 'Srážky:' },
  { names: ['textMinimumTemperature', 'textMaximumTemperature'], heading: 'Teploty:' },
]

// Kraje mají krátkodobý blok (CK0tx/CKntx/CK1tx/CK2tx = dnes/noc/zítra/pozítří) a blok
// "další dny" (CK3tx/CK4tx = pátek/sobota, viz kódy typů v cw-server/src/routes/forecast_meta.js).
const KRAJE_FURTHER_DAYS_CODES = new Set(['CK3tx', 'CK4tx'])

// Sousední typy předpovědi se mohou pro stejný den překrývat — např. "zítřejší" CR1tx
// vydané dnes ráno pokrývá stejný čtvrtek jako starší "pozítřejší" CR2tx vydané včera
// (jejich startTime/endTime se liší o pár hodin, protože nové vydání posune hranici dne),
// takže by se bez deduplikace zobrazily dvě karty "Předpověď na čtvrtek" (jedna zastaralá,
// s méně informacemi). Karty se proto seskupí podle textu `headline-main.headline`
// (obsahuje den v týdnu, u Hor rovnou i datum — spolehlivější klíč než přesná shoda
// startTime/endTime) a pro shodný headline se ponechá jen ta s nejnovějším `sent`.
function dedupeByHeadlineKeepNewest(forecasts) {
  const byHeadline = new Map()
  for (const f of forecasts) {
    const key = f.properties?.['headline-main']?.headline || f.type
    const existing = byHeadline.get(key)
    if (!existing || new Date(f.properties?.sent) > new Date(existing.properties?.sent)) {
      byHeadline.set(key, f)
    }
  }
  return [...byHeadline.values()]
}

// Chronologické pořadí podle startTime — nahrazuje spoléhání na pořadí `code` z API (to je
// jen abecední, takže by např. CKntx/noc skončilo za CK4tx místo hned za dnešním dnem).
function sortByStartTime(forecasts) {
  return [...forecasts].sort((a, b) => {
    const ta = new Date(a.properties?.['headline-main']?.startTime || 0)
    const tb = new Date(b.properties?.['headline-main']?.startTime || 0)
    return ta - tb
  })
}

function formatDateTime(value) {
  if (!value) return null
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return value
  return d.toLocaleString('cs-CZ', { dateStyle: 'medium', timeStyle: 'short' })
}

// dd.MM.rrrr (např. "16.09.2026") — jednotný formát rozsahu období v nadpisu stránky
// napříč kategoriemi (PageHeading), viz doc/plans/forecast-text-page.md.
//
// endOfInterval: `endTime` v datech ČHMÚ je EXKLUZIVNÍ konec intervalu — půlnoc na
// začátku dne NÁSLEDUJÍCÍHO po posledním pokrytém dni (ověřeno napříč CR/Kraje/Hory,
// např. CH1tx "16.9." má endTime "17.9. 00:00" lokálně). Zobrazit poslední pokrytý den
// tedy znamená při přesné půlnoci odečíst 1 den, jinak by "od 16.9. do 18.9." u dvoudenní
// předpovědi (16.–17.9.) mátlo o den navíc.
function formatPaddedDate(value, { endOfInterval = false } = {}) {
  if (!value) return null
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return null
  if (endOfInterval && d.getHours() === 0 && d.getMinutes() === 0 && d.getSeconds() === 0) {
    d.setDate(d.getDate() - 1)
  }
  const pad = n => String(n).padStart(2, '0')
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`
}

// Jméno(a) meteorologa napříč sadou karet — obvykle jedno, ale při opravě jiným
// meteorologem se sbírají všechny unikátní hodnoty, aby se žádné autorství neztratilo.
function collectSenderNames(forecasts) {
  const names = new Set()
  for (const f of forecasts) {
    const name = f.properties?.senderName
    if (name) names.add(name)
  }
  return [...names]
}

function DataItem({ item }) {
  return (
    <p className="text-sm text-gray-700 whitespace-pre-line">
      {item.headline && <span className="font-medium">{item.headline} </span>}
      {item.displayText}
    </p>
  )
}

// Základní render properties.data[] — položky s headline "Srážky ...HODNOTA" u Kraje/ČR se
// zobrazují jako headline+text na řádek, textIntro/text bez headline jako perex nad tím.
function GenericCardBody({ dataItems }) {
  const intro = dataItems.find(i => !i.headline)
  const rest = dataItems.filter(i => i !== intro)
  return (
    <div className="mt-2 flex flex-col gap-2">
      {intro && <p className="text-sm text-gray-800 font-medium">{intro.displayText}</p>}
      {rest.map((item, i) => <DataItem key={i} item={item} />)}
    </div>
  )
}

// Hory: textHeadline ("Stručný úvod:") jako perex, textPrecProbability+textPrecipitation
// sloučené pod "Srážky:", textMinimumTemperature+textMaximumTemperature pod "Teploty:",
// zbytek (Počasí/Vítr/Nebezpečné jevy…) samostatně — viz HORY_MERGED_GROUPS.
function HoryCardBody({ dataItems }) {
  const byName = new Map(dataItems.map(i => [i.name, i]))
  const consumed = new Set()
  const intro = dataItems.find(i => i.name === 'textHeadline')
  if (intro) consumed.add(intro.name)

  const blocks = []
  for (const group of HORY_MERGED_GROUPS) {
    const items = group.names.map(n => byName.get(n)).filter(Boolean)
    if (items.length === 0) continue
    items.forEach(i => consumed.add(i.name))
    blocks.push({ heading: group.heading, lines: items.map(i => i.displayText) })
  }

  const rest = dataItems.filter(i => !consumed.has(i.name))

  return (
    <div className="mt-2 flex flex-col gap-2">
      {intro && <p className="text-sm text-gray-800 font-medium">{intro.displayText}</p>}
      {blocks.map((b, i) => (
        <div key={i} className="text-sm text-gray-700">
          <span className="font-medium">{b.heading}</span>
          {b.lines.map((line, j) => <p key={j} className="whitespace-pre-line">{line}</p>)}
        </div>
      ))}
      {rest.map((item, i) => <DataItem key={i} item={item} />)}
    </div>
  )
}

function CategorySwitch({ category, onSelect }) {
  return (
    <div className="flex gap-2">
      {CATEGORIES.map(c => (
        <button
          key={c.value}
          onClick={() => onSelect(c.value)}
          className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
            category === c.value
              ? 'bg-blue-600 border-blue-600 text-white'
              : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
          }`}
        >
          {c.label}
        </button>
      ))}
    </div>
  )
}

function RegionSelect({ regions, region, onSelect }) {
  if (!regions || regions.length === 0) return null
  return (
    <select
      value={region || ''}
      onChange={e => onSelect(e.target.value)}
      className="text-sm px-3 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:border-blue-400"
    >
      {regions.map(r => (
        <option key={r.region} value={r.region}>{r.name}</option>
      ))}
    </select>
  )
}

function ForecastCard({ forecast, category }) {
  const sentAt = formatDateTime(forecast.properties?.sent || forecast.properties?.referenceTime)
  const senderName = forecast.properties?.senderName
  const heading = forecast.properties?.['headline-main']?.headline || forecast.title || forecast.code
  const dataItems = forecast.properties?.data || []
  const Body = category === 'Hory' ? HoryCardBody : GenericCardBody

  return (
    <div className="w-full bg-white border border-gray-200 rounded-2xl p-4 sm:p-5 shadow-sm">
      <div className="flex items-baseline justify-between gap-2 flex-wrap">
        <h2 className="font-semibold text-gray-800">{heading}</h2>
        {sentAt && (
          <span className="text-xs text-gray-400">
            Vydáno: {sentAt}{senderName ? ` – ${senderName}` : ''}
          </span>
        )}
      </div>
      {dataItems.length === 0
        ? <p className="mt-2 text-sm text-gray-400">Bez textu.</p>
        : <Body dataItems={dataItems} />}
    </div>
  )
}

// Nadpis stránky — jednotný vzor napříč kategoriemi: krátký název + řádek "od – do" s
// rozsahem pokrytého období (dd.MM.rrrr, viz formatPaddedDate). U Kraje se rozsah bere jen
// z krátkodobého bloku (dnes/noc/zítra), ne z celého (už chronologicky seřazeného) pole —
// to obsahuje i CK3tx/CK4tx z bloku "další dny".
function PageHeading({ category, forecasts, placeName }) {
  if (forecasts.length === 0) {
    return (
      <div className="text-center mt-10 sm:mt-0">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-800">Předpovědi</h1>
        <p className="text-gray-400 mt-1 text-sm sm:text-base">Textové předpovědi počasí ČHMÚ</p>
      </div>
    )
  }

  const headingForecasts = category === 'Kraje'
    ? forecasts.filter(f => !KRAJE_FURTHER_DAYS_CODES.has(f.code))
    : forecasts
  const first = headingForecasts[0] || forecasts[0]
  const last = headingForecasts[headingForecasts.length - 1] || forecasts[forecasts.length - 1]
  const from = formatPaddedDate(first.properties?.['headline-main']?.startTime)
  const to = formatPaddedDate(last.properties?.['headline-main']?.endTime, { endOfInterval: true })

  const title = category === 'CR' ? 'Předpověď počasí pro ČR'
    : category === 'Hory' ? `Předpověď počasí ${placeName}`
    : `Krátkodobá předpověď ${placeName}`

  return (
    <div className="text-center mt-10 sm:mt-0">
      <h1 className="text-xl sm:text-2xl font-bold text-gray-800">{title}</h1>
      {from && to && <p className="text-gray-400 mt-1 text-sm sm:text-base">od {from} do {to}</p>}
    </div>
  )
}

// "Situace:" text (typ CR8ts) — sdílený zdroj napříč kategoriemi: u ČR úvod k výhledu na
// týden, u Kraje navíc jako dodatek před blokem "Předpověď na další dny" (CK3tx/CK4tx).
function SituationBlock({ situationForecast }) {
  const item = situationForecast?.properties?.data?.find(i => i.name === 'textSituation')
  if (!item) return null
  return (
    <div className="w-full bg-white border border-gray-200 rounded-2xl p-4 sm:p-5 shadow-sm">
      <h2 className="font-semibold text-gray-800">{item.headline || 'Situace:'}</h2>
      <p className="mt-2 text-sm text-gray-700 whitespace-pre-line">{item.displayText}</p>
    </div>
  )
}

function Signature({ rppOffice, senderNames }) {
  if (!rppOffice && senderNames.length === 0) return null
  return (
    <p className="text-sm text-gray-500 text-center">
      == {rppOffice}{senderNames.length > 0 ? ` / ${senderNames.join(', ')}` : ''} ==
    </p>
  )
}

// Kategorie podle toho, na co uživatel klikl v hlavním menu (viz LayoutMain — 3 odkazy
// pod ikonou Předpovědi: ČR, poslední kraj, poslední pohoří), jinak naposledy zvolená
// kategorie (localStorage, viz useLastForecastSelection).
function initialCategory(navigatedState) {
  return navigatedState?.category || getLastForecastSelection().category
}

// Počáteční region pro Kraje/Hory: explicitní region z navigace (klik v menu na konkrétní
// kraj/pohoří) má přednost, jinak naposledy zvolený region PRO DANOU KATEGORII zvlášť
// (viz useLastForecastSelection — Kraje a Hory si drží svůj poslední region nezávisle,
// aby přepnutí kategorie tam a zpět nabídlo pokaždé to, co bylo vybrané naposledy).
function initialRegionByCategory(navigatedState) {
  const last = getLastForecastSelection()
  const result = { Kraje: last.regionByCategory.Kraje?.region ?? null, Hory: last.regionByCategory.Hory?.region ?? null }
  if (navigatedState?.category && navigatedState.region) {
    result[navigatedState.category] = navigatedState.region
  }
  return result
}

export function ForecastLayout() {
  const navigate = useNavigate()
  // Sledování přístupů (etapa K2) — předpověď je systémová část bez uzlu v registru.
  useTrackView('forecast', 'forecast')
  const location = useLocation()
  const { data, isLoading, isError } = useForecastCategories()
  const [category, setCategory] = useState(() => initialCategory(location.state))
  // Kraje a Hory mají KAŽDÝ SVŮJ vlastní region ve stavu (ne jednu sdílenou hodnotu) —
  // přepnutí kategorie tak rovnou zobrazí naposledy zvolený region té kategorie, místo
  // aby se muselo dopočítávat/resetovat na první ze seznamu při každém přepnutí.
  const [regionByCategory, setRegionByCategory] = useState(() => initialRegionByCategory(location.state))
  const region = regionByCategory[category] ?? null
  const setRegion = (r) => setRegionByCategory(prev => ({ ...prev, [category]: r }))

  const regions = category !== 'CR' ? data?.[category]?.regions || [] : []

  // Když pro aktuální kategorii ještě není platný region (úplně první vstup na stránku,
  // nebo uložený region už v seznamu regionů není), vybere první ze seznamu jako fallback.
  useEffect(() => {
    if (category === 'CR') return
    if (regions.length === 0) return
    if (!regions.some(r => r.region === region)) {
      setRegion(regions[0].region)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, regions])

  useEffect(() => {
    const regionName = regions.find(r => r.region === region)?.name
    setLastForecastSelection({ category, region: category === 'CR' ? null : region, regionName })
  }, [category, region, regions])

  const rawForecasts = category === 'CR'
    ? (data?.CR?.forecasts || []).filter(f => f.code !== 'CR8ts')
    : (region ? data?.[category]?.forecasts?.[region] || [] : [])
  // Deduplikace + chronologické řazení pro CR a Kraje — obě mají několik typů, jejichž
  // pokrytý den se může s dalším vydáním posunout a překrýt s jiným typem (viz komentář
  // u dedupeByHeadlineKeepNewest). Hory mají jen 2 karty bez překryvu, není co řešit.
  const forecasts = category === 'Hory' ? rawForecasts : sortByStartTime(dedupeByHeadlineKeepNewest(rawForecasts))

  const situationForecast = data?.CR?.forecasts?.find(f => f.code === 'CR8ts') || null
  const placeName = forecasts[0]?.properties?.place?.name || ''
  const rppOffice = category === 'CR' ? CR_RPP_OFFICE : (forecasts[0]?.rppOffice || null)
  const senderNames = collectSenderNames(forecasts)

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center p-4 sm:p-6 gap-4 sm:gap-6">
      <div className="fixed top-2 left-2 sm:top-4 sm:left-4 z-40 flex items-center gap-2">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 hover:border-gray-300 transition-colors text-sm text-gray-700"
          title="Domů"
        >
          <span className="text-lg leading-none">🏠</span> <span className="hidden sm:inline">Domů</span>
        </button>
        <UserIcon variant="compact" placement="bottom-left" />
      </div>

      <PageHeading category={category} forecasts={forecasts} placeName={placeName} />

      <div className="w-full max-w-2xl flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
        <CategorySwitch category={category} onSelect={setCategory} />
        {category !== 'CR' && (
          <RegionSelect regions={regions} region={region} onSelect={setRegion} />
        )}
      </div>

      <div className="w-full max-w-2xl flex flex-col gap-3">
        {isLoading && (
          <div className="flex items-center gap-2 text-sm text-gray-500 py-4 justify-center">
            <Spinner size="sm" /> Načítám předpovědi…
          </div>
        )}
        {isError && (
          <p className="text-red-500 text-sm text-center py-4">Chyba při načítání předpovědí.</p>
        )}
        {!isLoading && !isError && forecasts.length === 0 && (
          <p className="text-gray-400 text-sm text-center py-4">
            Pro tuto volbu nejsou k dispozici žádné předpovědi.
          </p>
        )}

        {category === 'CR' && <SituationBlock situationForecast={situationForecast} />}

        {category === 'Kraje' ? (
          <>
            {forecasts.filter(f => !KRAJE_FURTHER_DAYS_CODES.has(f.code)).map(f => (
              <ForecastCard key={f.type} forecast={f} category={category} />
            ))}
            {forecasts.some(f => KRAJE_FURTHER_DAYS_CODES.has(f.code)) && (
              <>
                <h2 className="text-lg font-semibold text-gray-700 mt-2">Předpověď na další dny</h2>
                <SituationBlock situationForecast={situationForecast} />
                {forecasts.filter(f => KRAJE_FURTHER_DAYS_CODES.has(f.code)).map(f => (
                  <ForecastCard key={f.type} forecast={f} category={category} />
                ))}
              </>
            )}
          </>
        ) : (
          forecasts.map(f => (
            <ForecastCard key={f.type} forecast={f} category={category} />
          ))
        )}

        {forecasts.length > 0 && <Signature rppOffice={rppOffice} senderNames={senderNames} />}
      </div>
    </div>
  )
}
